import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InvoiceAuditLogsTable } from "@/components/InvoiceAuditLogsTable";
import type { components } from "@dolfin/api-admin";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type InvoiceEventType = NonNullable<components["schemas"]["InvoiceEventType"]>;
type InvoiceState = NonNullable<components["schemas"]["InvoiceState"]>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const stateOptions: { value: InvoiceState | "any"; label: string }[] = [
  { value: "any", label: "Any state" },
  { value: "Draft", label: "Draft" },
  { value: "Issued", label: "Issued" },
  { value: "Paid", label: "Paid" },
  { value: "PartiallyPaid", label: "Partially paid" },
  { value: "Void", label: "Void" },
];

function parseState(raw: string | null): InvoiceState | "any" {
  return stateOptions.some((o) => o.value === raw) ? (raw as InvoiceState | "any") : "any";
}

// Only types InvoiceAuditLogConsumer writes: Sent/Paid/Voided are logged as InvoiceStateChanged, so filtering on
// them would return nothing.
const eventOptions: { value: InvoiceEventType | "any"; label: string }[] = [
  { value: "any", label: "All event types" },
  { value: "InvoiceCreated", label: "Invoice created" },
  { value: "InvoiceStateChanged", label: "State changed" },
  { value: "InvoiceReminderSent", label: "Reminder sent" },
  { value: "InvoiceReminderFailed", label: "Reminder failed" },
];

export function InvoiceAuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [orgIdInput, setOrgIdInput] = useState(searchParams.get("organisationId") ?? "");
  const debouncedOrgId = useDebouncedValue(orgIdInput.trim());

  const eventParam = searchParams.get("eventType");
  const eventType: InvoiceEventType | "any" = eventOptions.some((o) => o.value === eventParam)
    ? (eventParam as InvoiceEventType | "any")
    : "any";

  const fromState = parseState(searchParams.get("fromState"));
  const toState = parseState(searchParams.get("toState"));

  const [refreshKey, setRefreshKey] = useState(0);

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Only apply the org filter once it's a full UUID — the API expects uuid format.
  const organisationId = UUID_PATTERN.test(debouncedOrgId) ? debouncedOrgId : undefined;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Invoice Audit Logs</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Cross-organisation invoice audit trail: state transitions, reminders, and the actor behind
        each step. Newest first.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by organisation ID…"
          value={orgIdInput}
          onChange={(e) => setOrgIdInput(e.target.value)}
          aria-invalid={!!debouncedOrgId && !organisationId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Select
          value={eventType}
          onValueChange={(v) => updateParam("eventType", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {eventOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={fromState}
          onValueChange={(v) => updateParam("fromState", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="From state" />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.value === "any" ? "From: any state" : `From: ${opt.label}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={toState}
          onValueChange={(v) => updateParam("toState", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="To state" />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.value === "any" ? "To: any state" : `To: ${opt.label}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <InvoiceAuditLogsTable
        organisationId={organisationId}
        eventType={eventType === "any" ? undefined : eventType}
        fromState={fromState === "any" ? undefined : fromState}
        toState={toState === "any" ? undefined : toState}
        refreshKey={refreshKey}
      />
    </div>
  );
}
